import { NgModule, inject } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivateFn,
    Router,
    RouterModule,
    RouterStateSnapshot,
    UrlTree,
} from '@angular/router';
import { FeatureFlag } from '@app/enums';
import { AdminGuard, HasFeatureRedirectGuard } from '@app/guards';
import { RoutesWithOptions } from '@app/interfaces';
import { OnboardingStatusService } from './services/onboarding-status.service';
import {
    AddCompanyInfoView,
    AddEmployeesView,
    AddTeamView,
    AddYourTeamView,
    AdminProfileView,
    AuthorizationDocumentsView,
    BankAccountView,
    BookACallView,
    CompanyDetailsView,
    CompanyProfileView,
    CompleteYourProfileView,
    CraAccountView,
    EmployeeProfileBasicView,
    EmployeeProfileView,
    HomeView,
    PayYourTeamView,
    PayrollSituationView,
    ReviewView,
    SupportingDocumentsView,
    TellUsView,
    TimelineView,
} from './views';
import { PayrollDocumentsView } from './views/payroll-documents/payroll-documents.view';

/**
 * payYourTeamRedirect assists the user by guiding them to the first incomplete
 * step when they visit the "pay your team" section of the application.
 */
const payYourTeamRedirect: CanActivateFn = (_: ActivatedRouteSnapshot, state: RouterStateSnapshot): true | UrlTree => {
    // If they haven't landed directly on 'pay-your-team',
    // let them go where they want.
    if (!state.url.endsWith('pay-your-team')) {
        return true;
    }

    const router = inject(Router);
    const onboardingStatus = inject(OnboardingStatusService).onboardingStatus$.getValue();
    if (!onboardingStatus) {
        return true;
    }

    if (!onboardingStatus.payYourTeam.steps.craAccount) {
        return router.createUrlTree([state.url, 'cra-account']);
    }

    if (!onboardingStatus.payYourTeam.steps.bankAccount) {
        return router.createUrlTree([state.url, 'bank-account']);
    }

    if (!onboardingStatus.payYourTeam.steps.companyProfile) {
        return router.createUrlTree([state.url, 'company-profile']);
    }

    if (!onboardingStatus.payYourTeam.steps.adminProfile) {
        return router.createUrlTree([state.url, 'admin-profile']);
    }

    return router.createUrlTree([state.url, 'add-team']);
};

/**
 * payrollDocuments assists the user by guiding them to the first incomplete
 * step when they visit the "payroll documents" section of the application.
 */
const payrollDocumentsRedirect: CanActivateFn = (
    _: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
): true | UrlTree => {
    // If they haven't landed directly on 'payroll-documents',
    // let them go where they want.
    if (!state.url.endsWith('payroll-documents')) {
        return true;
    }

    const router = inject(Router);
    const onboardingStatus = inject(OnboardingStatusService).onboardingStatus$.getValue();
    if (!onboardingStatus) {
        return true;
    }

    if (!onboardingStatus.payrollDocuments.steps.authorizationDocuments) {
        return router.createUrlTree([state.url, 'authorization-documents']);
    }

    return router.createUrlTree([state.url, 'supporting-documents']);
};
/**
 * dashboardRedirect handles rerouting self serve admins to the dashboard
 * if they somehow land in company onboarding.
 */
const dashboardRedirect: CanActivateFn = (): true | UrlTree => {
    const onboardingStatusService = inject(OnboardingStatusService);

    if (!onboardingStatusService.hasActiveQuickstart()) {
        const router = inject(Router);
        return router.createUrlTree(['/']);
    }

    return true;
};

const addYourTeamRedirect: CanActivateFn = (_: ActivatedRouteSnapshot, state: RouterStateSnapshot): true | UrlTree => {
    // If they haven't landed directly on 'add-your-team',
    // let them go where they want.
    if (!state.url.endsWith('add-your-team')) {
        return true;
    }

    const router = inject(Router);
    const onboardingStatus = inject(OnboardingStatusService).onboardingStatus$.getValue();
    if (!onboardingStatus) {
        return true;
    }

    if (!onboardingStatus.addYourTeam.steps.completeYourProfile) {
        return router.createUrlTree([state.url, 'complete-your-profile']);
    }

    return router.createUrlTree([state.url, 'add-employees']);
};

const routes: RoutesWithOptions = [
    {
        path: '',
        component: HomeView,
        canActivate: [AdminGuard, dashboardRedirect],
    },
    {
        path: 'payroll-situation',
        component: PayrollSituationView,
        data: {
            hideSidebar: true,
        },
        children: [
            {
                path: '',
                redirectTo: 'tell-us',
                pathMatch: 'full',
                data: {
                    hideSidebar: true,
                },
            },
            {
                path: 'tell-us',
                component: TellUsView,
                data: {
                    hideSidebar: true,
                },
            },
            {
                path: 'timeline',
                component: TimelineView,
                data: {
                    hideSidebar: true,
                },
            },
        ],
    },
    {
        path: 'pay-your-team',
        component: PayYourTeamView,
        canActivate: [payYourTeamRedirect],
        data: {
            hideSidebar: true,
        },
        children: [
            {
                path: 'cra-account',
                component: CraAccountView,
                data: {
                    hideSidebar: true,
                },
            },
            {
                path: 'bank-account',
                component: BankAccountView,
                data: {
                    hideSidebar: true,
                },
            },
            {
                path: 'company-profile',
                component: CompanyProfileView,
                data: {
                    hideSidebar: true,
                },
            },
            {
                path: 'admin-profile',
                component: AdminProfileView,
                data: {
                    hideSidebar: true,
                },
            },
            {
                path: 'add-team',
                children: [
                    {
                        path: '',
                        component: AddTeamView,
                        data: {
                            hideSidebar: true,
                        },
                    },
                    {
                        path: 'new',
                        component: EmployeeProfileView,
                        data: {
                            hideSidebar: true,
                        },
                    },
                    {
                        path: 'new-terminated',
                        component: EmployeeProfileView,
                        data: {
                            hideSidebar: true,
                        },
                    },
                ],
            },
        ],
    },
    {
        path: 'book-a-call',
        component: BookACallView,
        data: {
            hideSidebar: true,
        },
        children: [
            {
                path: '',
                redirectTo: 'review',
                pathMatch: 'full',
                data: {
                    hideSidebar: true,
                },
            },
            {
                path: 'review',
                component: ReviewView,
                data: {
                    hideSidebar: true,
                },
            },
        ],
    },
    {
        path: 'payroll-documents',
        component: PayrollDocumentsView,
        canActivate: [payrollDocumentsRedirect],
        data: {
            hideSidebar: true,
        },
        children: [
            {
                path: 'authorization-documents',
                component: AuthorizationDocumentsView,
                data: {
                    hideSidebar: true,
                },
            },
            {
                path: 'supporting-documents',
                component: SupportingDocumentsView,
                canActivate: [HasFeatureRedirectGuard],
                data: {
                    hideSidebar: true,
                    feature: {
                        flags: [FeatureFlag.KYC_KYB],
                        redirectTo: ['company-onboarding', 'payroll-documents', 'authorization-documents'],
                    },
                },
            },
        ],
    },
    {
        path: 'company-details',
        component: CompanyDetailsView,
        data: {
            hideSidebar: true,
        },
        children: [
            {
                path: '',
                redirectTo: 'add-company-info',
                pathMatch: 'full',
                data: {
                    hideSidebar: true,
                },
            },
            {
                path: 'add-company-info',
                component: AddCompanyInfoView,
                data: {
                    hideSidebar: true,
                },
            },
        ],
    },
    {
        path: 'add-your-team',
        component: AddYourTeamView,
        canActivate: [addYourTeamRedirect],
        data: {
            hideSidebar: true,
        },
        children: [
            {
                path: 'complete-your-profile',
                component: CompleteYourProfileView,
                data: {
                    hideSidebar: true,
                },
            },
            {
                path: 'add-employees',
                children: [
                    {
                        path: '',
                        component: AddEmployeesView,
                        data: {
                            hideSidebar: true,
                        },
                    },
                    {
                        path: 'new',
                        component: EmployeeProfileBasicView,
                        data: {
                            hideSidebar: true,
                        },
                    },
                ],
            },
        ],
    },
];

@NgModule({
    imports: [RouterModule.forChild(routes)],
    exports: [RouterModule],
})
export class SelfServeRouterModule {}
